import {
  Checkbox,
  DivisionIllustration,
  EmptyState,
  Grid,
  GridRow,
  ModalBottomButtons,
  Tooltip,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import type { ClassGroupMembersUpdateFieldInput, UpdateClassGroupMutationVariables } from 'client/gql/graphql';
import { Form, Formik } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import type { BpClassType, BpDivisionType } from 'pages/Institution/subpages/InstitutionClassesSubpage';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import styles from './ClassGroupProfilesForm.module.scss';

const UpdateClassGroupMutationDocument = graphql(`
  mutation UpdateClassGroupInClassGroupProfilesForm($update: ClassGroupUpdateInput!, $where: ClassGroupWhere!) {
    updateClassGroups(update: $update, where: $where) {
      classGroups {
        uuid
        __typename
        members {
          uuid
          __typename
        }
      }
    }
  }
`);

const ClassGroupProfilesFormPageQueryDocument = graphql(`
  query ClassGroupProfilesFormPageData($organizationUuid: ID!, $classUuid: ID!) {
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      displayName
    }
    divisions(where: { class: { uuid: $classUuid }, organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      name
      groups {
        uuid
        __typename
      }
      managedBy
    }
    classGroups(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      name
      shortName
      division {
        uuid
        __typename
      }
      membersConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
          }
        }
      }
    }
  }
`);

type ClassGroupProfilesFormProps = {
  bpClass: BpClassType;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

type ProfileFormType = {
  uuid: string;
  name: string;
};

type GroupFormType = {
  uuid: string;
  name: string;
  shortName: string;
  profiles: ProfileFormType[];
};

type DivisionFormType = {
  uuid: string;
  name: string;
  profiles: ProfileFormType[];
  groups: GroupFormType[];
};

type ProfilesFormType = {
  divisions: DivisionFormType[];
};

export const ClassGroupProfilesForm = ({ bpClass, onLoad, onClose }: ClassGroupProfilesFormProps) => {
  const { t } = useTranslation();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();

  const [, updateGroup] = useMutation(UpdateClassGroupMutationDocument);

  const [{ data }] = useQuery({
    query: ClassGroupProfilesFormPageQueryDocument,
    variables: { organizationUuid, classUuid: bpClass.uuid },
  });
  const profiles = data?.profiles;
  const divisionsData = data?.divisions;
  const classGroups = data?.classGroups;

  const divisions: BpDivisionType[] =
    divisionsData?.map((d) => {
      return {
        uuid: d.uuid,
        name: d.name,
        classUuid: bpClass.uuid,
        classGroupUuids: d.groups.map((g) => g.uuid),
        source: d.managedBy,
      };
    }) ?? [];

  const initialValues: ProfilesFormType = {
    divisions: divisions.map((division) => {
      const divisionGroups = classGroups?.filter((g) => g.division.uuid === division.uuid) ?? [];
      const classProfiles = profiles?.filter((p) => bpClass.profileUuids.some((c) => c === p.uuid)) ?? [];
      return {
        ...division,
        profiles: classProfiles.map((p) => {
          return {
            uuid: p.uuid,
            name: p.displayName ?? '',
          };
        }),
        groups: divisionGroups.map((g) => {
          const groupProfiles =
            profiles?.filter((p) => g.membersConnection.edges.some((edge) => edge.node.uuid === p.uuid)) ?? [];
          return {
            uuid: g.uuid,
            name: g.name,
            shortName: g.shortName,
            profiles: groupProfiles.map((p) => {
              return {
                uuid: p.uuid,
                name: p.displayName ?? '',
              };
            }),
          };
        }),
      };
    }),
  };

  const handleSubmit = async (values: ProfilesFormType) => {
    onLoad(true);

    const groupsToEdit: UpdateClassGroupMutationVariables[] = [];

    values.divisions.forEach((division) => {
      const initialDivisions = initialValues.divisions.find((d) => d.uuid === division.uuid);

      division.groups.forEach((group) => {
        const initialGroup = initialDivisions?.groups.find((g) => g.uuid === group.uuid);
        const profilesToConnect: ClassGroupMembersUpdateFieldInput[] = [];
        const profilesToDisconnect: ClassGroupMembersUpdateFieldInput[] = [];

        division.profiles.forEach((profile) => {
          group?.profiles
            .filter((p) => p.uuid === profile.uuid && !initialGroup?.profiles.some((p) => p.uuid === profile.uuid))
            .forEach((profile) => {
              profilesToConnect.push({
                connect: [
                  {
                    where: {
                      node: {
                        uuid: profile.uuid,
                      },
                    },
                  },
                ],
              });
            });

          initialGroup?.profiles
            .filter((p) => p.uuid === profile.uuid && !group?.profiles.some((p) => p.uuid === profile.uuid))
            .forEach((profile) => {
              profilesToDisconnect.push({
                disconnect: [
                  {
                    where: {
                      node: {
                        uuid: profile.uuid,
                      },
                    },
                  },
                ],
              });
            });
        });

        if (profilesToConnect.length > 0 || profilesToDisconnect.length > 0) {
          groupsToEdit.push({
            update: {
              members: [...profilesToConnect, ...profilesToDisconnect],
            },
            where: {
              uuid: group.uuid,
            },
          });
        }
      });
    });

    let updateFinished = 0;
    for (const update of groupsToEdit) {
      const result = await updateGroup(update);
      if (result.error) {
        showErrorToast(result.error);
        return;
      }
      updateFinished++;
    }
    if (updateFinished > 0) {
      showSuccessToast(
        t('classGroups.groupEdited', {
          count: updateFinished,
          groups: groupsToEdit.length,
        }),
      );
    }

    onLoad(false);
    onClose();
  };

  return (
    <Formik<ProfilesFormType> onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize={true}>
      {({ errors, values, isSubmitting, dirty, isValidating, handleSubmit, setFieldValue }) => {
        return (
          <Form className={styles['class-group-profiles-form']}>
            <Grid useFormGap>
              {values.divisions.length === 0 ? (
                <EmptyState forcedHeight='30vh' icon={<DivisionIllustration />} title={t('divisions.noDivisions')} />
              ) : (
                values.divisions.map((division, divisionIndex) => {
                  const gridWidth = `repeat(${division.groups.length}, 70px)`;
                  const countHint = `${division.groups.reduce((acc, curr) => {
                    return acc + curr.profiles.length;
                  }, 0)}/${division.profiles.length}`;

                  return (
                    <GridRow headline={division.name} key={division.uuid}>
                      <div className={styles.division}>
                        <div className={styles.header} style={{ gridTemplateColumns: `200px ${gridWidth}` }}>
                          <div>
                            {t('students.title', { count: 2 })}
                            <div className={styles.count}>{countHint}</div>
                          </div>
                          {division.groups.map((group) => {
                            return (
                              <Tooltip
                                key={group.uuid}
                                content={group.name}
                                triggerClass={styles['group-name']}
                                usePortal
                              >
                                <div className={styles.name}>{group.shortName}</div>
                                <div className={styles.count}>{group.profiles.length}</div>
                              </Tooltip>
                            );
                          })}
                        </div>

                        {division.profiles.length === 0 ? (
                          <EmptyState
                            size='small'
                            hideIcon
                            forcedHeight='10vh'
                            title={t('classes.noProfiles')}
                            subtitle={t('classes.noProfilesHint')}
                          />
                        ) : (
                          division.profiles.map((profile) => {
                            return (
                              <div
                                key={profile.uuid}
                                className={styles.profile}
                                style={{
                                  gridTemplateColumns: `200px ${gridWidth}`,
                                }}
                              >
                                <div className={styles['student-name']}>{profile.name}</div>
                                {division.groups.map((group, groupIndex) => {
                                  return (
                                    <div key={profile.uuid + '-' + group.uuid}>
                                      <Checkbox
                                        className={styles.checkbox}
                                        name={`${profile.name} in ${group.name}`}
                                        onChange={async (value) => {
                                          const currentGroup = values.divisions[divisionIndex].groups[groupIndex];

                                          if (value.target.checked) {
                                            currentGroup.profiles.push(profile);
                                          } else {
                                            currentGroup.profiles = currentGroup.profiles.filter(
                                              (p) => p.uuid !== profile.uuid,
                                            );
                                          }

                                          const currentDivision = values.divisions[divisionIndex];
                                          currentDivision.groups = currentDivision.groups.map((g) => {
                                            return g.uuid === currentGroup.uuid ? currentGroup : g;
                                          });

                                          const newDivisions = values.divisions.map((d) => {
                                            return d.uuid === currentDivision.uuid ? currentDivision : d;
                                          });

                                          await setFieldValue('divisions', newDivisions);
                                        }}
                                        checked={group.profiles.some((g) => g.uuid === profile.uuid)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </GridRow>
                  );
                })
              )}
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              submitButton={{
                disabled: isSubmitting || !dirty || isValidating,
                callback: () => {
                  handleSubmit();
                },
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
